import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clock, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResourceDetail() {
  const { id } = useParams();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/resources/${id}`)
      .then((r) => setResource(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="resource-loading">
        <Loader2 className="w-8 h-8 text-teal animate-spin" />
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-white" data-testid="resource-not-found">
        <div className="text-center max-w-md px-6">
          <h1 className="font-display text-3xl text-navy mb-4">Article not found</h1>
          <p className="text-muted-ink mb-8">This resource may have moved or been removed.</p>
          <Link to="/resources" className="btn-primary">
            <ArrowLeft className="w-4 h-4" /> Back to Resources
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="resource-detail-page" className="bg-white">
      <article>
        {/* HERO */}
        <section className="py-16 lg:py-20 bg-gradient-hero" data-testid="resource-hero">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <Link
              to="/resources"
              className="inline-flex items-center gap-2 text-teal font-bold text-sm mb-8 hover:gap-3 transition-all"
              data-testid="back-to-resources"
            >
              <ArrowLeft className="w-4 h-4" /> All resources
            </Link>
            <p className="eyebrow mb-4">{resource.category}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05]">
              {resource.title}
            </h1>
            <div className="mt-6 flex items-center gap-4 text-sm text-muted-ink">
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {resource.read_time}</span>
              <span className="w-1 h-1 rounded-full bg-muted-ink" />
              <span className="font-semibold">By the GH care team</span>
            </div>
          </div>
        </section>

        {/* BODY */}
        <section className="py-12 lg:py-16" data-testid="resource-body">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <div className="text-xl text-navy font-medium leading-relaxed mb-12 pb-12 border-b border-slate-200">
              {resource.summary}
            </div>

            <div className="space-y-10">
              {resource.sections?.map((s, i) => (
                <div key={i} data-testid={`resource-section-${i}`}>
                  {s.heading && (
                    <h2 className="font-display text-2xl sm:text-3xl text-navy mb-5 leading-tight">{s.heading}</h2>
                  )}
                  <div className="space-y-5 text-[17px] text-muted-ink leading-[1.75]">
                    {s.paragraphs.map((p, pi) => (
                      <p key={pi}>{p}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-cream" data-testid="resource-cta">
          <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
            <h3 className="font-display text-3xl sm:text-4xl text-navy mb-5 leading-[1.1]">
              Want to talk to <span className="hl-teal">someone real?</span>
            </h3>
            <p className="text-muted-ink mb-8">
              The Generational Health AI Companion can guide you through this — and connect you to a human supporter or licensed clinician any hour of the day.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/download" className="btn-primary" data-testid="resource-cta-download">
                Get the app <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/resources" className="btn-outline" data-testid="resource-cta-more">
                More resources
              </Link>
            </div>
          </div>
        </section>
      </article>
    </div>
  );
}
